import { type JSX, useEffect, useState } from 'react';

import { MobiLauncherConfig } from '@schema/index.js';
import type { MobiLauncherConfig as ParsedConfig } from '@schema/index.js';

import {
  fetchConfig,
  fetchLicenseByTenant,
  type ConfigBlob,
  openBillingPortal,
  type PortalLicense,
  saveTenantConfig,
} from '../api.js';
import {
  buildBundle,
  type BundleResult,
  downloadBundle,
  publishHostedBundle,
} from '../bundle.js';
import { BuilderEditor } from '../builder/BuilderEditor.js';
import { Tour } from '../tour/Tour.js';
import { resetSeen } from '../tour/persistence.js';
import { EDITOR_STEPS, EDITOR_TOUR_ID } from '../tour/tours.js';
import { AuditPanel } from './AuditPanel.js';
import { BroadcastsEditor } from './BroadcastsEditor.js';
import { DevicesPanel } from './DevicesPanel.js';
import { TelemetryPanel } from './TelemetryPanel.js';
import { UpgradePanel } from './UpgradePanel.js';
import type { TeamRole } from '../api.js';
import { TeamPanel } from './TeamPanel.js';

export interface ConfigEditorProps {
  tenantId: string;
  /** Owner bearer; null for a non-owner admin (writes via the session cookie). */
  editToken: string | null;
  launcherUrl: string;
  onBack: () => void;
  /** The signed-in customer's role on this tenant. Owners get the Team +
   *  Upgrade/billing tabs; admins edit but can't manage billing or the team. */
  role?: TeamRole;
}

/* ───── Editable view-model ─────
 * The full MobiLauncherConfig has many fields; this minimal editor
 * exposes just the surface a Free customer cares about. Everything
 * else is passed through opaquely so the next save preserves whatever
 * was there (theme colors, status bar, admin, meta, etc.).
 */

type EditableTileType = 'app' | 'web';

interface EditableTile {
  id: string;
  type: EditableTileType;
  label: string;
  /** packageName when type==='app', url when type==='web' */
  target: string;
}

type EditableMdm = 'androidEnterprise' | 'mobicontrol' | 'none';

interface EditableModel {
  deploymentName: string;
  /** Which MDM owns this deployment — drives Device Info dialog field set. */
  mdm: EditableMdm;
  profileName: string;
  columns: number;
  rows: number;
  tiles: EditableTile[];
}

interface EditableFromConfig {
  model: EditableModel;
  /** The original config blob — we mutate selected fields and re-PUT. */
  base: ConfigBlob;
}

type View =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: EditableFromConfig }
  | { kind: 'saving'; data: EditableFromConfig }
  | { kind: 'saved'; data: EditableFromConfig; at: number }
  | { kind: 'save-error'; data: EditableFromConfig; message: string };

export type EditorTab = 'launcher' | 'broadcasts' | 'devices' | 'telemetry' | 'audit' | 'upgrade' | 'team';

export interface ConfigEditorExtraProps {
  /**
   * When provided, the editor opens on this tab on mount. App.tsx uses
   * this to jump straight to "Upgrade" after a successful Stripe redirect.
   */
  initialTab?: EditorTab;
  /** When provided, renders a one-shot banner above the tabs. */
  banner?: { kind: 'success' | 'info'; message: string };
  /**
   * Dashboard-embedded mode: the surrounding FleetDashboard owns navigation, so
   * the active tab is controlled via `activeTab` and the editor's own internal
   * tab strip + section header are hidden (the sidebar replaces them).
   */
  embedded?: boolean;
  activeTab?: EditorTab;
}

export function ConfigEditor({
  tenantId,
  editToken,
  launcherUrl,
  onBack,
  role,
  initialTab,
  banner,
  embedded = false,
  activeTab,
}: ConfigEditorProps & ConfigEditorExtraProps): JSX.Element {
  // Viewers get read-only access to the monitoring panels (devices, telemetry,
  // audit, broadcasts list). The launcher-authoring tab is hidden for them, so
  // they land on Devices.
  const readOnly = role === 'viewer';
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [tab, setTab] = useState<EditorTab>(
    activeTab ?? initialTab ?? (readOnly ? 'devices' : 'launcher'),
  );

  // In embedded mode the active tab is driven by the dashboard route.
  useEffect(() => {
    if (embedded && activeTab !== undefined) setTab(activeTab);
  }, [embedded, activeTab]);
  const [license, setLicense] = useState<PortalLicense | null>(null);
  const [rawLicenseBlob, setRawLicenseBlob] = useState<string>('');
  const [billingPortalError, setBillingPortalError] = useState<string | null>(null);
  /**
   * Flips to true the first time the LivePreview iframe inside the
   * BuilderEditor sends `preview-ready`. The first-run tour gates its
   * `run` prop on this so step 4 ("Live preview") doesn't fire against
   * an empty iframe. Lives here (above the outer-tab boundary) so a
   * tour step that switches to Broadcasts / Devices / Upgrade —
   * which unmounts BuilderEditor — doesn't take the tour with it.
   * Once flipped, stays true even when the launcher tab unmounts.
   */
  const [previewReady, setPreviewReady] = useState(false);
  /**
   * Bumped when the user clicks "Take the tour" — combined with a
   * `key={tourRunCount}` on <Tour>, re-mounts the component so its
   * hasSeen() check re-evaluates against fresh localStorage (which
   * we just cleared via resetSeen). Cheaper than a full page reload
   * and preserves scroll position.
   */
  const [tourRunCount, setTourRunCount] = useState(0);

  // Fetch the current license once per tenant. We don't block the editor
  // on this — failures fall through to "Core" treatment in the Upgrade
  // tab + a "Upgrade to unlock offline bundles" disabled state on the
  // Launcher tab's bundle button. Raw blob is captured separately so
  // `buildBundle` can drop it verbatim into the ZIP (Ed25519 signature
  // must round-trip byte-for-byte to verify on-device).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await fetchLicenseByTenant(tenantId);
      if (cancelled) return;
      if (res.kind === 'ok') {
        setLicense(res.license);
        setRawLicenseBlob(res.rawBlob);
      } else {
        setLicense(null);
        setRawLicenseBlob('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleManageSubscription(): Promise<void> {
    setBillingPortalError(null);
    // Billing is owner-only, enforced by the owner-gated tab + the server's
    // role gate. Authorized via the session cookie (editToken is null now).
    const res = await openBillingPortal({
      tenantId,
      editToken,
      returnUrl: `${window.location.origin}/?tenant=${encodeURIComponent(tenantId)}`,
    });
    if (res.kind === 'ok') {
      window.location.assign(res.url);
      return;
    }
    if (res.kind === 'no-subscription') {
      setBillingPortalError('No Stripe subscription on file yet.');
      return;
    }
    if (res.kind === 'unauthorized') {
      setBillingPortalError('Edit token rejected.');
      return;
    }
    if (res.kind === 'stripe') {
      setBillingPortalError(`Stripe: ${res.message}`);
      return;
    }
    if (res.kind === 'network') {
      setBillingPortalError(`Network: ${res.message}`);
      return;
    }
    setBillingPortalError(res.message);
  }

  const currentTier: 'core' | 'pro' | 'enterprise' = license?.tier ?? 'core';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await fetchConfig(tenantId);
      if (cancelled) return;
      if (res.kind === 'ok') {
        const model = extractEditable(res.config);
        if (model === null) {
          setView({
            kind: 'error',
            message: 'Config is in an unexpected shape; cannot edit safely from the Portal.',
          });
          return;
        }
        setView({ kind: 'ready', data: { model, base: res.config } });
        return;
      }
      if (res.kind === 'not-found') {
        setView({
          kind: 'error',
          message:
            'No config found for this tenant. Re-sign up with the same email to seed one.',
        });
        return;
      }
      setView({
        kind: 'error',
        message: res.kind === 'network' ? res.message : `Server returned ${String(res.status)}`,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (view.kind === 'loading') {
    return (
      <section className="details" data-testid="editor-loading">
        <p className="details__hint">Loading your config…</p>
      </section>
    );
  }
  if (view.kind === 'error') {
    return (
      <section className="error" role="alert">
        <h2 className="error__title">Couldn&apos;t load your config</h2>
        <p className="error__body">{view.message}</p>
        <button type="button" className="error__again" onClick={onBack}>
          Back
        </button>
      </section>
    );
  }

  const data = view.data;

  function update(patch: Partial<EditableModel>): void {
    setView({ kind: 'ready', data: { ...data, model: { ...data.model, ...patch } } });
  }
  function updateTile(idx: number, patch: Partial<EditableTile>): void {
    const tiles = data.model.tiles.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    update({ tiles });
  }
  function removeTile(idx: number): void {
    update({ tiles: data.model.tiles.filter((_, i) => i !== idx) });
  }
  function addTile(): void {
    update({
      tiles: [
        ...data.model.tiles,
        {
          id: crypto.randomUUID(),
          type: 'app',
          label: 'New tile',
          target: 'com.example.app',
        },
      ],
    });
  }

  const capacity = data.model.columns * data.model.rows;
  const atCapacity = data.model.tiles.length >= capacity;
  const validation = validate(data.model);

  async function handleSave(): Promise<void> {
    setView({ kind: 'saving', data });
    const merged = mergeIntoBase(data.base, data.model);
    const res = await saveTenantConfig({ tenantId, editToken, config: merged });
    if (res.kind === 'ok') {
      setView({ kind: 'saved', data: { ...data, base: merged }, at: res.updatedAt });
      return;
    }
    let message: string;
    switch (res.kind) {
      case 'unauthorized':
        message = 'Edit token rejected. Re-sign up with the same email to recover.';
        break;
      case 'invalid':
        message = `Server rejected: ${res.issues[0]?.message ?? 'schema validation failed'}`;
        break;
      case 'tenantMismatch':
        message = res.message;
        break;
      case 'network':
        message = `Network: ${res.message}`;
        break;
      case 'unexpected':
        message = `Server returned ${String(res.status)}: ${res.message}`;
        break;
    }
    setView({ kind: 'save-error', data, message });
  }

  const busy = view.kind === 'saving';

  // On the Launcher tab, mark the section so CSS can drop the card
  // chrome and let the embedded Builder fill the available width.
  // Note: we deliberately don't add the `.editor` class anymore — its
  // 38rem max-width (defined in the Builder's stylesheet for each
  // section's internal form) was wrongly constraining the whole
  // section here, leaving Broadcasts / Upgrade tabs squeezed into
  // ~608px while the Portal main was already 80rem wide.
  const sectionClassName =
    tab === 'launcher' ? 'details details--builder' : 'details';

  // Per-tab section header copy. The header sits above the tab strip
  // (so it stays visible while the user switches tabs), and each tab's
  // panel no longer carries its own duplicate header inside.
  const headerCopy = (() => {
    switch (tab) {
      case 'launcher':
        return {
          title: 'Customize your launcher',
          sub: 'Edits save to your tenant slot and take effect on next reload.',
        };
      case 'broadcasts':
        return {
          title: 'Broadcasts',
          sub: "Short messages devices show in the launcher's bell-icon panel. Active broadcasts appear within a minute of posting.",
        };
      case 'devices':
        return {
          title: 'Devices',
          sub: 'Print a QR sticker to onboard a new kiosk, or scroll the registered devices list to see what’s already in the fleet.',
        };
      case 'telemetry':
        return {
          title: 'Telemetry',
          sub: "Verify your fleet is reporting events to the hosted ingest endpoint. Point at your own observability stack when you're ready.",
        };
      case 'audit':
        return {
          title: 'Audit log',
          sub: 'Tamper-evident record of admin actions, gesture and PIN events, and runtime state changes across your fleet.',
        };
      case 'upgrade':
        return currentTier === 'core'
          ? {
              title: 'Upgrade your fleet',
              sub: 'Pro unlocks theming, broadcasts, scheduled profiles. Enterprise adds remote config, telemetry, SSO display, and signage mode.',
            }
          : {
              title: 'Subscription',
              sub: 'Manage your plan, payment method, and invoices.',
            };
      case 'team':
        return {
          title: 'Team',
          sub: 'Invite teammates and control their access to this fleet.',
        };
    }
  })();

  return (
    <section className={sectionClassName} data-testid="config-editor">
      {!embedded && (
      <header className="details__head">
        <span className={`details__tier-badge details__tier-badge--${currentTier}`}>
          {currentTier}
        </span>
        <div className="details__head-text">
          <h2 className="details__title">{headerCopy.title}</h2>
          <p className="details__sub">{headerCopy.sub}</p>
        </div>
        {/* Re-trigger the first-run tour. Clears the localStorage
            seen-flag and bumps a key on <Tour> to force remount —
            cheaper than a full reload and preserves scroll position.
            Lives next to the tier badge so it's always discoverable
            without crowding the title block. */}
        <button
          type="button"
          className="details__tour-btn"
          onClick={() => {
            resetSeen(EDITOR_TOUR_ID);
            setTourRunCount((n) => n + 1);
          }}
          title="Replay the first-run tour"
          data-testid="config-editor-tour-replay"
        >
          ↺ Tour
        </button>
      </header>
      )}

      {banner && (
        <p
          className={
            banner.kind === 'success' ? 'details__bundle-ok' : 'details__hint'
          }
          role={banner.kind === 'success' ? 'status' : undefined}
          data-testid="editor-banner"
        >
          {banner.message}
        </p>
      )}

      {readOnly && (
        <p className="details__hint" role="status" data-testid="editor-readonly-banner">
          Read-only — you’re a viewer on this fleet. You can monitor devices, telemetry, and
          broadcasts, but not make changes.
        </p>
      )}

      {!embedded && (
      <div className="portal__tabs" role="tablist" aria-label="Editor section">
        {!readOnly && (
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'launcher'}
          className={`portal__tab${tab === 'launcher' ? ' portal__tab--active' : ''}`}
          onClick={() => setTab('launcher')}
          data-testid="editor-tab-launcher"
        >
          Launcher
        </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'broadcasts'}
          className={`portal__tab${tab === 'broadcasts' ? ' portal__tab--active' : ''}`}
          onClick={() => setTab('broadcasts')}
          data-testid="editor-tab-broadcasts"
        >
          Broadcasts
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'devices'}
          className={`portal__tab${tab === 'devices' ? ' portal__tab--active' : ''}`}
          onClick={() => setTab('devices')}
          data-testid="editor-tab-devices"
        >
          Devices
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'telemetry'}
          className={`portal__tab${tab === 'telemetry' ? ' portal__tab--active' : ''}`}
          onClick={() => setTab('telemetry')}
          data-testid="editor-tab-telemetry"
        >
          Telemetry
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'audit'}
          className={`portal__tab${tab === 'audit' ? ' portal__tab--active' : ''}`}
          onClick={() => setTab('audit')}
          data-testid="editor-tab-audit"
        >
          Audit
        </button>
        {role === 'owner' && (
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'upgrade'}
          className={`portal__tab${tab === 'upgrade' ? ' portal__tab--active' : ''}`}
          onClick={() => setTab('upgrade')}
          data-testid="editor-tab-upgrade"
        >
          {currentTier === 'core' ? 'Upgrade' : 'Subscription'}
        </button>
        )}
        {role === 'owner' && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'team'}
            className={`portal__tab${tab === 'team' ? ' portal__tab--active' : ''}`}
            onClick={() => setTab('team')}
            data-testid="editor-tab-team"
          >
            Team
          </button>
        )}
      </div>
      )}

      {tab === 'broadcasts' && (
        <>
          <BroadcastsEditor tenantId={tenantId} editToken={editToken} readOnly={readOnly} />
          <div className="details__actions">
            <button type="button" className="details__again" onClick={onBack}>
              Back
            </button>
          </div>
        </>
      )}

      {tab === 'devices' && (
        <>
          <DevicesPanel
            launcherUrl={launcherUrl}
            tenantId={tenantId}
            editToken={editToken}
            readOnly={readOnly}
          />
          <div className="details__actions">
            <button type="button" className="details__again" onClick={onBack}>
              Back
            </button>
          </div>
        </>
      )}

      {tab === 'team' && role === 'owner' && (
        <>
          <TeamPanel tenantId={tenantId} />
          <div className="details__actions">
            <button type="button" className="details__again" onClick={onBack}>
              Back
            </button>
          </div>
        </>
      )}

      {tab === 'telemetry' && (
        <>
          <TelemetryPanel
            tenantId={tenantId}
            editToken={editToken}
            currentTier={currentTier}
          />
          <div className="details__actions">
            <button type="button" className="details__again" onClick={onBack}>
              Back
            </button>
          </div>
        </>
      )}

      {tab === 'audit' && (
        <>
          <AuditPanel
            tenantId={tenantId}
            editToken={editToken}
            currentTier={currentTier}
          />
          <div className="details__actions">
            <button type="button" className="details__again" onClick={onBack}>
              Back
            </button>
          </div>
        </>
      )}

      {tab === 'upgrade' && role === 'owner' && (
        <>
          {currentTier !== 'core' && (
            <section
              className="details upgrade-current"
              data-testid="upgrade-current"
            >
              <p>
                You&apos;re on{' '}
                <strong>{currentTier === 'pro' ? 'Pro' : 'Enterprise'}</strong>.
                Manage your card, switch plan, or cancel anytime.
              </p>
              {billingPortalError !== null && (
                <p className="details__bundle-err" role="alert">
                  {billingPortalError}
                </p>
              )}
              <div className="details__actions">
                <button
                  type="button"
                  className="details__bundle"
                  onClick={() => {
                    void handleManageSubscription();
                  }}
                  data-testid="manage-subscription"
                >
                  Manage subscription
                </button>
              </div>
            </section>
          )}
          {currentTier !== 'enterprise' && (
            <UpgradePanel
              tenantId={tenantId}
              editToken={editToken}
              currentTier={currentTier}
            />
          )}
          <div className="details__actions">
            <button type="button" className="details__again" onClick={onBack}>
              Back
            </button>
          </div>
        </>
      )}

      {tab === 'launcher' && (
        <LauncherTabPanel
          data={data}
          tenantId={tenantId}
          editToken={editToken}
          launcherUrl={launcherUrl}
          currentTier={currentTier}
          rawLicenseBlob={rawLicenseBlob}
          onSwitchTab={setTab}
          onBack={onBack}
          onPreviewReady={() => setPreviewReady(true)}
        />
      )}
      {/*
        First-run editor tour. Mounted at the ConfigEditor level (not
        inside BuilderEditor) so a step whose `before` hook clicks the
        Broadcasts / Devices / Upgrade outer tab — which unmounts
        BuilderEditor — doesn't take the running Joyride instance
        with it. `run` is gated on previewReady (signalled by the
        BuilderEditor's LivePreview) so step 4 doesn't fire against
        an empty iframe.
      */}
      <Tour key={tourRunCount} id={EDITOR_TOUR_ID} steps={EDITOR_STEPS} run={previewReady} />
    </section>
  );
}

/* ───── Extract / merge helpers ───── */

function extractEditable(config: ConfigBlob): EditableModel | null {
  const profiles = config['profiles'];
  if (!Array.isArray(profiles) || profiles.length === 0) return null;
  const first = profiles[0] as Record<string, unknown> | undefined;
  if (!first) return null;
  const grid = first['grid'] as Record<string, unknown> | undefined;
  if (!grid) return null;
  const rawTiles = grid['tiles'];
  if (!Array.isArray(rawTiles)) return null;

  const tiles: EditableTile[] = [];
  for (const raw of rawTiles) {
    const t = raw as Record<string, unknown>;
    const type = t['type'];
    const id = t['id'];
    const label = t['label'];
    if (typeof id !== 'string' || typeof label !== 'string') continue;
    if (type === 'app' && typeof t['packageName'] === 'string') {
      tiles.push({ id, type: 'app', label, target: t['packageName'] });
    } else if (type === 'web' && typeof t['url'] === 'string') {
      tiles.push({ id, type: 'web', label, target: t['url'] });
    }
    // spacer / agentAction tiles are preserved opaquely in base; not
    // exposed in the editor for v1.
  }

  const rawMdm = config['mdm'];
  const mdm: EditableMdm =
    rawMdm === 'mobicontrol' ? 'mobicontrol' : rawMdm === 'androidEnterprise' ? 'androidEnterprise' : 'none';

  return {
    deploymentName: typeof config['deploymentName'] === 'string' ? config['deploymentName'] : '',
    mdm,
    profileName: typeof first['name'] === 'string' ? first['name'] : '',
    columns: typeof grid['columns'] === 'number' ? grid['columns'] : 4,
    rows: typeof grid['rows'] === 'number' ? grid['rows'] : 3,
    tiles,
  };
}

function mergeIntoBase(base: ConfigBlob, model: EditableModel): ConfigBlob {
  // Deep clone the base so we don't mutate the original UI state.
  const next = JSON.parse(JSON.stringify(base)) as ConfigBlob;
  next['deploymentName'] = model.deploymentName;
  next['mdm'] = model.mdm;

  const profiles = Array.isArray(next['profiles']) ? (next['profiles'] as unknown[]) : [];
  if (profiles.length === 0) return next;
  const profile = profiles[0] as Record<string, unknown>;
  profile['name'] = model.profileName;

  const grid = (profile['grid'] as Record<string, unknown>) ?? {};
  grid['columns'] = model.columns;
  grid['rows'] = model.rows;

  // Preserve non-app/non-web tiles from the original (spacer, agentAction)
  // by tile id; otherwise replace with the editor's view.
  const originalTiles = Array.isArray(grid['tiles']) ? (grid['tiles'] as unknown[]) : [];
  const preservedById = new Map<string, unknown>();
  for (const raw of originalTiles) {
    const t = raw as Record<string, unknown>;
    if (t['type'] !== 'app' && t['type'] !== 'web' && typeof t['id'] === 'string') {
      preservedById.set(t['id'], raw);
    }
  }
  const editedTiles: unknown[] = model.tiles.map((t) =>
    t.type === 'app'
      ? { type: 'app', id: t.id, label: t.label, packageName: t.target }
      : { type: 'web', id: t.id, label: t.label, url: t.target },
  );
  // Tack any preserved tiles onto the end so they aren't lost.
  for (const raw of preservedById.values()) editedTiles.push(raw);
  grid['tiles'] = editedTiles;
  profile['grid'] = grid;
  profiles[0] = profile;
  next['profiles'] = profiles;

  return next;
}

function validate(model: EditableModel): string | null {
  if (model.deploymentName.trim().length === 0) return 'Deployment name is required.';
  if (model.profileName.trim().length === 0) return 'Profile name is required.';
  if (model.tiles.length === 0) return 'At least one tile is required.';
  if (model.tiles.length > model.columns * model.rows)
    return `Too many tiles for ${String(model.columns)}×${String(model.rows)} grid.`;
  for (const t of model.tiles) {
    if (t.label.trim().length === 0) return 'Every tile needs a label.';
    if (t.target.trim().length === 0)
      return t.type === 'app' ? 'Every app tile needs a package name.' : 'Every web tile needs a URL.';
    if (t.type === 'web' && !/^https?:\/\//.test(t.target)) {
      return 'Web tile URLs must start with http:// or https://';
    }
  }
  return null;
}

function clamp(n: number, min: number, max: number, fallback: number): number {
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/* ───── Launcher tab — full Builder editor (Phase 1 of the merge) ───── */

/** State machine for the Launcher tab's "Download offline bundle" button. */
type BundleState =
  | { kind: 'idle' }
  | { kind: 'building' }
  | { kind: 'ready'; result: Extract<BundleResult, { kind: 'ok' }> }
  | { kind: 'error'; message: string };

type HostedState =
  | { kind: 'idle' }
  | { kind: 'publishing' }
  | { kind: 'ready'; downloadUrl: string; downloadToken: string }
  | { kind: 'error'; message: string };

interface LauncherTabPanelProps {
  data: EditableFromConfig;
  tenantId: string;
  editToken: string | null;
  launcherUrl: string;
  /** Current license tier — drives tier-locked section gating in the Builder. */
  currentTier: 'core' | 'pro' | 'enterprise';
  /**
   * Raw signed-license JSON — passed verbatim into the offline ZIP so
   * the Ed25519 signature verifies on-device. Empty string while the
   * license is still being fetched OR for Core tenants (who have no
   * signed license — the bundle button is gated off in that case).
   */
  rawLicenseBlob: string;
  /** Switch the ConfigEditor's outer tab — used by the lock banner's CTA. */
  onSwitchTab: (tab: EditorTab) => void;
  onBack: () => void;
  /**
   * Forwarded to BuilderEditor → LivePreview. The ConfigEditor owns
   * the previewReady state because the first-run Tour lives there
   * (above the outer-tab boundary).
   */
  onPreviewReady: () => void;
}

function LauncherTabPanel({
  data,
  tenantId,
  editToken,
  launcherUrl,
  currentTier,
  rawLicenseBlob,
  onSwitchTab,
  onBack,
  onPreviewReady,
}: LauncherTabPanelProps): JSX.Element {
  // Parse the raw config blob into a typed Config the BuilderEditor
  // can edit. The blob was already validated server-side on the last
  // PUT, but the Portal stores it as opaque JSON — a re-parse here
  // gives us safety + type-narrowed editing without trusting the
  // wire payload blindly.
  const parsed = MobiLauncherConfig.safeParse(data.base);
  if (!parsed.success) {
    return (
      <p className="details__bundle-err" role="alert">
        Config doesn't parse — the merged Builder can't edit it safely.
        Issues:{' '}
        {parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}
      </p>
    );
  }
  const [bundle, setBundle] = useState<BundleState>({ kind: 'idle' });
  const [hosted, setHosted] = useState<HostedState>({ kind: 'idle' });
  const canBundle = currentTier !== 'core' && rawLicenseBlob.length > 0;

  async function handleDownloadBundle(): Promise<void> {
    setBundle({ kind: 'building' });
    const result = await buildBundle({
      tenantId,
      tier: currentTier,
      rawLicenseBlob,
    });
    if (result.kind === 'ok') {
      setBundle({ kind: 'ready', result });
      downloadBundle(result);
      return;
    }
    setBundle({ kind: 'error', message: result.message });
  }

  async function handlePublishHosted(): Promise<void> {
    // Hosted-bundle publish now accepts the session cookie (owner or admin, edit
    // capability) — no editToken needed.
    setHosted({ kind: 'publishing' });
    const built = await buildBundle({ tenantId, tier: currentTier, rawLicenseBlob });
    if (built.kind !== 'ok') {
      setHosted({ kind: 'error', message: built.message });
      return;
    }
    const res = await publishHostedBundle({ tenantId, editToken, blob: built.blob });
    if (res.kind === 'ok') {
      setHosted({ kind: 'ready', downloadUrl: res.downloadUrl, downloadToken: res.downloadToken });
      return;
    }
    setHosted({ kind: 'error', message: res.message });
  }

  return (
    <>
      <BuilderEditor
        tenantId={tenantId}
        editToken={editToken}
        initialConfig={parsed.data as ParsedConfig}
        currentTier={currentTier}
        onUpgradeClick={() => onSwitchTab('upgrade')}
        onPreviewReady={onPreviewReady}
      />
      <div className="details__actions">
        <a
          className="details__download"
          href={launcherUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open my launcher
        </a>
        {canBundle ? (
          <button
            type="button"
            className="details__bundle"
            onClick={() => void handleDownloadBundle()}
            disabled={bundle.kind === 'building'}
            data-testid="launcher-bundle-download"
          >
            {bundle.kind === 'building'
              ? 'Assembling bundle…'
              : bundle.kind === 'ready'
                ? 'Re-download offline bundle'
                : 'Download offline bundle'}
          </button>
        ) : null}
        {canBundle ? (
          <button
            type="button"
            className="details__bundle"
            onClick={() => void handlePublishHosted()}
            disabled={hosted.kind === 'publishing'}
            title="Host the signed bundle at a URL your file server can sync from"
            data-testid="launcher-bundle-publish"
          >
            {hosted.kind === 'publishing'
              ? 'Publishing…'
              : hosted.kind === 'ready'
                ? 'Re-publish hosted bundle'
                : 'Publish hosted bundle'}
          </button>
        ) : (
          <button
            type="button"
            className="details__bundle details__bundle--locked"
            onClick={() => onSwitchTab('upgrade')}
            title="Offline bundles are a Pro/Enterprise feature"
            data-testid="launcher-bundle-upgrade"
          >
            🔒 Upgrade for offline bundle
          </button>
        )}
        <button type="button" className="details__again" onClick={onBack}>
          Back
        </button>
      </div>
      {bundle.kind === 'ready' && (
        <p className="details__bundle-ok" role="status">
          ✓ Saved <code>{bundle.result.filename}</code>. Drop it into your
          MDM's file-sync / sideload payload and point a kiosk-mode
          browser at the unpacked <code>index.html</code>.
        </p>
      )}
      {bundle.kind === 'error' && (
        <p className="details__bundle-err" role="alert">
          {bundle.message}
        </p>
      )}
      {hosted.kind === 'ready' && (
        <div className="details__bundle-ok" role="status">
          <p>
            ✓ Hosted bundle published. Point your file server's sync job at this
            URL to refresh the MobiControl File Sync share:
          </p>
          <p>
            <code data-testid="hosted-download-url">{hosted.downloadUrl}</code>
          </p>
          <p>
            Download token (keep it secret — it grants bundle access and can be
            rotated by re-publishing after revoking): <code>{hosted.downloadToken}</code>
          </p>
        </div>
      )}
      {hosted.kind === 'error' && (
        <p className="details__bundle-err" role="alert">
          {hosted.message}
        </p>
      )}
    </>
  );
}
